import { useClientPortalSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  DangerButton,
  DangerTag,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DropdownMenu,
  ExpandableCard,
  MenuItem,
  Modal,
  Stack,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { Key, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props {
  initialValues: components["schemas"]["AtsIntegrationDetail"];
  reload: () => void;
}

export function WorkableIntegrationHeader({ initialValues, reload }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDisableModalVisible, setDisableModalVisible] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

  const { submit: disable } = useClientPortalSubmit(
    `/api/internal/client-portal/ats-integrations/${initialValues.id}/disable/`,
    {
      onSuccess: () => {
        showToast(
          t("Success, {{integration}} has been successfully disabled.", {
            integration: "Workable",
          }),
          {
            type: "success",
          }
        );
        reload();
      },
      showToastOnError: true,
    }
  );

  const { submit: enable } = useClientPortalSubmit(
    `/api/internal/client-portal/ats-integrations/${initialValues.id}/enable/`,
    {
      onSuccess: () => {
        showToast(
          t("Success, {{integration}} has been successfully enabled.", {
            integration: "Workable",
          }),
          {
            type: "success",
          }
        );
        reload();
      },
      showToastOnError: true,
    }
  );

  const { submit: doDelete } = useClientPortalSubmit(
    `/api/internal/client-portal/ats-integrations/${initialValues.id}/delete/`,
    {
      onSuccess: () => {
        showToast(
          t("{{integration}} integration has been successfully deleted", {
            integration: "Workable",
          }),
          {
            type: "success",
          }
        );
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  function handleItemAction(actionId: Key) {
    if (actionId === "delete") {
      setDeleteModalVisible(true);
    } else {
      if (initialValues.is_active) setDisableModalVisible(true);
      else enable();
    }
  }

  return (
    <>
      <ExpandableCard
        title={
          <Stack orientation="horizontal" gap="space-2">
            Workable
            {initialValues.is_active ? (
              <SuccessTag type="subtle">{t("Enabled")}</SuccessTag>
            ) : (
              <DangerTag type="subtle">{t("Disabled")}</DangerTag>
            )}
          </Stack>
        }
        subtitle={t("Active since {{createdDt}}", {
          createdDt: formatDate(initialValues.created),
        })}
        rightContent={
          <DropdownMenu buttonIcon="more_vert" onItemAction={handleItemAction}>
            <MenuItem id="action">
              {initialValues.is_active ? t("Disable") : t("Enable")}
            </MenuItem>
            <MenuItem id="delete" textColor="text-danger">
              {t("Delete")}
            </MenuItem>
          </DropdownMenu>
        }
      />
      <Modal isOpen={isDeleteModalVisible}>
        <Dialog width="560px">
          <DialogHeader onClose={() => setDeleteModalVisible(false)}>
            {t("Delete {{integration}} integration", {
              integration: "Workable",
            })}
          </DialogHeader>
          <DialogContent>
            <Text style="form/body-primary">
              {t(
                "Deleting the integration will permanently stop all data sharing between Certn and {{integration}}. Cases already in progress will still be completed within Certn, but their data will no longer be shared with {{integration}}. All integration settings will also be removed.",
                { integration: "Workable" }
              )}
            </Text>
          </DialogContent>
          <DialogFooter>
            <BasicButton onPress={() => setDeleteModalVisible(false)}>
              {t("Cancel")}
            </BasicButton>
            <DangerButton
              size="medium"
              onPress={() => {
                doDelete();
                setDeleteModalVisible(false);
              }}
              aria-label={t("Close button")}
              testID="delete-workable-modal-close-button"
            >
              {t("Delete integration")}
            </DangerButton>
          </DialogFooter>
        </Dialog>
      </Modal>
      <Modal isOpen={isDisableModalVisible}>
        <Dialog width="560px">
          <DialogHeader onClose={() => setDisableModalVisible(false)}>
            {t("Disable integration")}
          </DialogHeader>
          <DialogContent>
            <Text style="form/body-primary">
              {t(
                "Disabling the integration will stop all data sharing between Certn and {{integration}}. Any cases already in progress will continue to be completed, but their data will no longer be shared with {{integration}}.",
                { integration: "Workable" }
              )}
            </Text>
          </DialogContent>
          <DialogFooter>
            <BasicButton onPress={() => setDisableModalVisible(false)}>
              {t("Cancel")}
            </BasicButton>
            <DangerButton
              size="medium"
              onPress={() => {
                disable();
                setDisableModalVisible(false);
              }}
              aria-label={t("Close button")}
              testID="disable-workable-modal-close-button"
            >
              {t("Disable integration")}
            </DangerButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </>
  );
}
